import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown, Check } from 'lucide-react';
import PruhSPrejezdem from './PruhSPrejezdem';
import NabidkaAkci from './NabidkaAkci';
import { MODAL_SKLO } from '../../lib/uiTokens';
import { skloMaterial, SkloCtx } from '../ui/Sklo';

/**
 * ListToolbar — STANDARDNÍ HLAVIČKA SEZNAMU (norma FofrLama OS).
 *
 * Vždy stejné rozmístění, ať je seznam v kterémkoli modulu:
 *
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ 🔍 hledání  [⚙ Filtr 2]                [+ Přidat] [+ Kategorie]│  ← hledání + filtr, akce vpravo
 *   │ (Aktivní 81) (Marže pod 30 % 65) (Archiv 3)                    │  ← pohledy (pills)
 *   │ Kanál: Pokladna ×  Marže: pod 30 % ×  Zrušit vše               │  ← aktivní filtry (jen když jsou)
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Pohled = předem definovaná sada dat (vždy právě jeden, pill). Filtr =
 * vlastní zúžení té sady (tlačítko vedle hledání, viz `ListFiltr`).
 * Ovládání zobrazení seznamu (rozbalit / sbalit strom) sem nepatří — sedí
 * v hlavičce seznamu přímo nad šipkami, které ovládá.
 *
 * `siroky` přepíná mezi **zmenšenou** verzí (úzký sloupec vedle detailu —
 * akce jen jako ikony, krátký placeholder) a **expandovanou** (přehled přes
 * celou šířku — akce s popiskem, širší pole hledání). Obojí je stejná
 * hlavička, jen jinak zhuštěná; nikdy se nepřeskládává.
 *
 * `kompaktni` je třetí hustota — **mobil**. Hlavička tam nescrolluje pryč (je to
 * navigace celé obrazovky), takže musí ubrat na výšce: menší odsazení a pohledy
 * v jedné řadě, kterou jde přejet prstem. Rozmístění zůstává i tady stejné —
 * hledání vlevo, akce vpravo, pohledy pod tím.
 *
 * @param {boolean} [props.siroky] - expandovaná verze (přehled přes celou šířku)
 * @param {boolean} [props.kompaktni] - mobilní hustota (nižší, pohledy v jedné řadě)
 * @param {{value: string, onChange: (v: string) => void, placeholder?: string, placeholderKratky?: string}} props.hledani
 * @param {Array<{id: string, label: string, Icon: Function, onClick: Function, primarni?: boolean, title?: string}>} [props.akce]
 *        Akce se kreslí vpravo nahoře v pořadí, v jakém přijdou. `primarni`
 *        (max jedna) je barevně vyplněná — hlavní „přidat" akce modulu.
 * @param {React.ReactNode} [props.nastroje] - nástroje modulu vpravo za akcemi
 *        (statistiky, report, šablony) — typicky `NabidkaAkci` pod „…".
 *        Patří sem, ne do seznamu: seznam drží entitu modulu, tohle jsou
 *        nástroje nad ní. V seznamu o 436 řádcích by se k nim nikdo nedorolo­val.
 * @param {Array<{id, label, Icon, onClick}>} [props.nastrojeAkce] - totéž jako
 *        `nastroje`, ale **jako data**. Kdo je předá takhle, dostane na mobilu
 *        jednu logiku: nahoře **hledání a filtr**, všechno ostatní (akce modulu
 *        i nástroje) pod jedním „…".
 *
 *        Důvod: na 402px displeji se do řádku vešlo hledání zúžené na pět
 *        písmen a vedle něj pět ovladačů — filtr, nahrát, přidat, Statistiky
 *        s popiskem a ještě „…". Šest cílů v řadě, z toho tři skryté pod tím
 *        samým znakem. Hledání je přitom to jediné, co se v seznamu o stovkách
 *        dokladů používá pořád. Na širokém rozvržení se nic neslučuje —
 *        tam je místa dost a akce patří na povrch.
 *
 *        Předávat obojí (`nastroje` i `nastrojeAkce`) nemá smysl: vznikla by
 *        dvě „…" vedle sebe.
 * @param {React.ReactNode} [props.filtr] - tlačítko filtru hned vedle hledání (`FiltrTlacitko`)
 * @param {React.ReactNode} [props.pohled] - předdefinované pohledy vlevo (`PohledPill`),
 *        každý jako vlastní pilulka. Pro čtyři až šest pohledů je to nejrychlejší
 *        ovládání, jaké jde mít: sada je vidět celá a přepnutí je jedno klepnutí.
 * @param {Array<{id, label, pocet?, Icon?, upozorneni?, barva?, title?, skupina?}>} [props.pohledy]
 *        Pohledy **jako data** — místo `pohled`. Řádek se pak nesloží z pilulek,
 *        ale z ovladače s aktivním pohledem a zkratek na to, co hlásí problém;
 *        zbytek je v nabídce, seskupený podle `skupina`.
 *
 *        Důvod: pilulka za každý pohled přestane fungovat, jakmile jich je deset.
 *        V Dokladech zabíraly tři řady (78 px) nad prvním řádkem seznamu a hlavně
 *        kreslily deset rovnocenných věcí, ačkoli to jsou tři různé otázky —
 *        kde doklad leží, co je na něm špatně a co s ním mám udělat rukama.
 *        Řada s ovladačem **nikdy nezalomí**, ať je pohledů kolik chce.
 *
 *        Zkratku dostane pohled, který má `upozorneni` **a** `barva` (tedy sám
 *        o sobě hlásí, že něco není v pořádku) a zrovna něco obsahuje. Na širokém
 *        rozvržení tři, v úzkém sloupci dvě — jinak by se řádek zase zalomil.
 * @param {string} [props.pohledAktivni] - id vybraného pohledu (jen s `pohledy`)
 * @param {(id: string) => void} [props.onPohled] - přepnutí pohledu (jen s `pohledy`)
 * @param {React.ReactNode} [props.aktivniFiltry] - řádek zapnutých filtrů (`AktivniFiltry`)
 * @param {React.ReactNode} [props.pohledVpravo] - nastavení pohledu vpravo (sbalit vše)
 * @param {React.ReactNode} [props.pod] - vlastní pruh pod hlavičkou (např. „vybráno N")
 * @param {string} props.barva - akcentová barva modulu (SECTION_COLOR)
 * @param {boolean} [props.sklo] - jen s `kompaktni`: horní ovládání na mobilu
 *        ve skle (`Sklo.jsx`) — hledání jako skleněná pilulka, akce jako kruhy,
 *        hlavní akce plný kruh v barvě sekce, bez linky pod hlavičkou.
 *        Zatím Komunikace; až se osvědčí, stane se mobilní výchozí.
 */
export default function ListToolbar({
    siroky = false,
    kompaktni = false,
    isDark,
    mutedColor,
    inputStyle,
    hledani,
    akce = [],
    nastroje = null,
    nastrojeAkce = null,
    filtr = null,
    pohled = null,
    pohledy = null,
    pohledAktivni = null,
    onPohled = null,
    pohledVpravo = null,
    aktivniFiltry = null,
    pod = null,
    barva = '#F59E0B',
    sklo: skloProp = false,
}) {
    const hodnota = hledani?.value ?? '';
    const sklo = skloProp && kompaktni;

    // Zkratka je vyhrazená pohledu, který hlásí problém (`upozorneni` + vlastní
    // `barva`) a něco v něm je. Prázdný pohled se nenabízí — „Po splatnosti 0"
    // by zabralo místo a neřeklo nic, co by nestálo v nabídce.
    const zkratky = (pohledy || [])
        .filter(p => p.upozorneni && p.barva && (p.pocet ?? 0) > 0 && p.id !== pohledAktivni)
        .slice(0, siroky ? 3 : 2);
    const vybrany = (pohledy || []).find(p => p.id === pohledAktivni) || (pohledy || [])[0];

    const tlacitko = (a) => {
        // `key` se předává přímo, ne spreadem — React na to jinak hlásí varování
        // a v budoucí verzi by se klíč ztratil.
        const spolecne = {
            onClick: a.onClick,
            title: a.title || a.label,
            'aria-label': a.label,
            className: 'flex items-center justify-center rounded-lg',
            style: sklo ? {
                // Sklo: kruh 44 px, hlavní akce plná v barvě sekce
                ...(a.primarni ? { background: barva, color: '#fff', boxShadow: `0 6px 18px ${isDark ? 'rgba(0,0,0,0.4)' : 'rgba(30,30,50,0.2)'}` }
                    : { ...skloMaterial(isDark), color: 'var(--text-primary)' }),
                border: 'none', borderRadius: 999, width: 44, height: 44, flexShrink: 0, cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
            } : {
                border: a.primarni ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                background: a.primarni ? barva : 'transparent',
                color: a.primarni ? '#fff' : 'var(--text-secondary, #94a3b8)',
                cursor: 'pointer',
                flexShrink: 0,
                // Na dotyku 40×40 místo 32×32 — pořád pod doporučeným minimem,
                // ale tlačítka sedí u horní hrany, kde se nescrolluje, a víc by
                // už ukrojilo z pole hledání.
                height: kompaktni ? 40 : 32,
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                padding: siroky ? '0 12px' : 0,
                width: siroky ? 'auto' : (kompaktni ? 40 : 32),
            },
        };
        return (
            <button key={a.id} {...spolecne}>
                <a.Icon size={sklo ? 20 : 16} strokeWidth={sklo && a.primarni ? 2.4 : 2} />
                {siroky && <span>{a.label}</span>}
            </button>
        );
    };

    return (
        <SkloCtx.Provider value={sklo}>
        <div className={kompaktni ? 'space-y-1.5' : 'p-3 space-y-2'}
            style={{
                flexShrink: 0,
                ...(kompaktni ? { padding: '8px 10px' } : null),
                borderBottom: sklo ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
            }}>
            {/* Řádek 1 — hledání vlevo, akce vpravo */}
            <div className="flex items-center gap-2">
                <div style={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: siroky ? 420 : 'none' }}>
                    <Search size={sklo ? 17 : 14} style={{ position: 'absolute', left: sklo ? 15 : 11, top: sklo ? 13.5 : (kompaktni ? 13 : 10), color: mutedColor, pointerEvents: 'none', zIndex: 1 }} />
                    <input
                        value={hodnota}
                        onChange={e => hledani?.onChange?.(e.target.value)}
                        placeholder={siroky ? (hledani?.placeholder || 'Hledat…') : (hledani?.placeholderKratky || hledani?.placeholder || 'Hledat…')}
                        style={{
                            ...inputStyle, paddingLeft: 32, paddingRight: hodnota ? 36 : undefined,
                            ...(kompaktni ? { height: 40, fontSize: 15 } : { fontSize: 12 }),
                            // Sklo: pilulka 44 px bez rámečku, stejný materiál jako kruhy vedle
                            ...(sklo ? { ...skloMaterial(isDark), height: 44, borderRadius: 999, border: 'none', paddingLeft: 42, fontSize: 16 } : null),
                        }}
                    />
                    {hodnota && (
                        // Křížek v poli je jediný cíl, kam se palec trefuje „naslepo" —
                        // dostane plochu, i když ikona zůstane malá.
                        <button onClick={() => hledani?.onChange?.('')} title="Zrušit hledání" aria-label="Zrušit hledání"
                            style={{
                                position: 'absolute', right: 0, top: 0, bottom: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: kompaktni ? 40 : 28,
                                background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0,
                            }}>
                            <X size={kompaktni ? 16 : 13} />
                        </button>
                    )}
                </div>
                {filtr}
                {(akce.length > 0 || nastroje || nastrojeAkce?.length > 0) && (
                    <div className="flex items-center gap-2" style={{ marginLeft: 'auto' }}>
                        {nastrojeAkce?.length > 0 && kompaktni ? (
                            /* Mobil: hledání a filtr nahoře, zbytek pod jedním „…". */
                            <NabidkaAkci akce={[...akce, ...nastrojeAkce]} jenNabidka
                                isMobile isDark={isDark} barva={barva} />
                        ) : (
                            <>
                                {akce.map(tlacitko)}
                                {nastroje}
                                {nastrojeAkce?.length > 0 && (
                                    <NabidkaAkci akce={nastrojeAkce} kolikVidet={0}
                                        isMobile={kompaktni} isDark={isDark} barva={barva} />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {pod}

            {/* Řádek 2 — předdefinované pohledy. Na mobilu se nezalamují do tří
                řad (to by z hlavičky udělalo půl obrazovky), ale jedou v jedné
                řadě, kterou jde přejet prstem. */}
            {pohledy?.length > 0 ? (
                /* Řada s ovladačem — jeden řádek, ať je pohledů kolik chce. */
                <div className="flex items-center" style={{ gap: 6, minHeight: kompaktni ? 38 : 28, flexWrap: 'nowrap' }}>
                    <PohledOvladac pohledy={pohledy} vybrany={vybrany} onVyber={onPohled}
                        barva={barva} isDark={isDark} mutedColor={mutedColor} kompaktni={kompaktni} />
                    <div style={{ flex: 1, minWidth: 0 }} />
                    {zkratky.map(p => (
                        <PohledZkratka key={p.id} pohled={p} siroky={siroky} kompaktni={kompaktni}
                            onClick={() => onPohled?.(p.id)} />
                    ))}
                    {pohledVpravo && <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>{pohledVpravo}</div>}
                </div>
            ) : (pohled || pohledVpravo) && (
                <div className="flex items-center gap-1.5" style={{ minHeight: kompaktni ? 36 : 26, flexWrap: kompaktni ? 'nowrap' : 'wrap' }}>
                    {kompaktni ? (
                        // Mezera 10 px místo 6: šest cílů v jedné řadě se jinak
                        // dotykem pletlo a následek (přepnutá sada dat) mate.
                        // `PruhSPrejezdem` dá najevo, že pruh pokračuje — bez
                        // něj poslední pilulka mizí za hranou a nic to neřekne.
                        <PruhSPrejezdem className="flex items-center"
                            style={{ gap: 10, flex: 1, minWidth: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            {pohled}
                        </PruhSPrejezdem>
                    ) : pohled}
                    {pohledVpravo && <div className="flex items-center gap-2" style={{ marginLeft: 'auto', flexShrink: 0 }}>{pohledVpravo}</div>}
                </div>
            )}

            {/* Řádek 3 — zapnuté filtry (komponenta vrací null, když žádné nejsou) */}
            {aktivniFiltry}
        </div>
        </SkloCtx.Provider>
    );
}

/**
 * Tlačítko do řádku „nastavení pohledu" — textové, bez rámečku, aktivní stav
 * akcentovou barvou. Aby přepínače pohledu vypadaly ve všech modulech stejně.
 */
export function PohledTlacitko({ Icon, label, aktivni = false, onClick, title, barva = '#F59E0B', mutedColor }) {
    return (
        <button onClick={onClick} title={title || label}
            className="flex items-center gap-1 text-[11px] font-medium rounded-md"
            style={{
                background: aktivni ? `${barva}1f` : 'none',
                border: 'none', cursor: 'pointer',
                color: aktivni ? barva : mutedColor,
                padding: '2px 6px',
            }}
            onMouseEnter={e => { if (!aktivni) e.currentTarget.style.color = barva; }}
            onMouseLeave={e => { if (!aktivni) e.currentTarget.style.color = mutedColor; }}>
            {Icon && <Icon size={13} />}
            {label}
        </button>
    );
}

/**
 * Ovladač pohledu — aktivní pohled s počtem, po klepnutí seskupená nabídka.
 *
 * Náhrada za řadu pilulek tam, kde jich je tolik, že se řada zalomí. Nabídka
 * je seskupená podle `skupina` v pořadí, v jakém se skupiny poprvé objeví —
 * u Dokladů „Fronty / Co je špatně / Moje práce", tedy tři otázky, na které
 * se pohledy ptají. Pilulkový řádek je uměl jen vyjmenovat vedle sebe.
 */
export function PohledOvladac({ pohledy = [], vybrany, onVyber, barva = '#F59E0B', isDark, mutedColor, kompaktni = false }) {
    const [otevreno, setOtevreno] = useState(false);
    const [pozice, setPozice] = useState(null);
    const tlacitkoRef = useRef(null);
    const panelRef = useRef(null);

    const spocitejPozici = () => {
        const r = tlacitkoRef.current?.getBoundingClientRect();
        if (!r) return;
        const sirka = Math.max(232, Math.min(300, r.width * 2));
        const left = Math.min(Math.max(8, r.left), window.innerWidth - sirka - 8);
        setPozice({ left, top: r.bottom + 6, sirka, maxVyska: window.innerHeight - r.bottom - 20 });
    };

    useLayoutEffect(() => { if (otevreno) spocitejPozici(); }, [otevreno]);

    useEffect(() => {
        if (!otevreno) return;
        const mimo = (e) => {
            if (tlacitkoRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            setOtevreno(false);
        };
        const klavesa = (e) => { if (e.key === 'Escape') setOtevreno(false); };
        document.addEventListener('mousedown', mimo);
        document.addEventListener('keydown', klavesa);
        window.addEventListener('resize', spocitejPozici);
        window.addEventListener('scroll', spocitejPozici, true);
        return () => {
            document.removeEventListener('mousedown', mimo);
            document.removeEventListener('keydown', klavesa);
            window.removeEventListener('resize', spocitejPozici);
            window.removeEventListener('scroll', spocitejPozici, true);
        };
    }, [otevreno]);

    if (!vybrany) return null;

    // Skupiny v pořadí prvního výskytu — pohled bez `skupina` spadne do prvního
    // oddílu bez nadpisu, ať se nikdy neztratí.
    const skupiny = [];
    for (const p of pohledy) {
        const nazev = p.skupina || '';
        let sk = skupiny.find(x => x.nazev === nazev);
        if (!sk) { sk = { nazev, polozky: [] }; skupiny.push(sk); }
        sk.polozky.push(p);
    }

    const vyber = (id) => { onVyber?.(id); setOtevreno(false); };

    return (
        <>
            <button ref={tlacitkoRef} type="button" onClick={() => setOtevreno(o => !o)}
                aria-haspopup="menu" aria-expanded={otevreno} title="Vybrat pohled"
                className="inline-flex items-center rounded-full font-semibold"
                style={{
                    gap: 6, flexShrink: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                    height: kompaktni ? 36 : 28, padding: kompaktni ? '0 10px 0 14px' : '0 8px 0 11px',
                    fontSize: kompaktni ? 13 : 11.5,
                    border: `1px solid ${barva}${otevreno ? 'bf' : '88'}`,
                    background: `${barva}${otevreno ? '33' : '1f'}`,
                    color: barva,
                    WebkitTapHighlightColor: 'transparent',
                }}>
                {vybrany.Icon && <vybrany.Icon size={kompaktni ? 14 : 12} />}
                {vybrany.label}
                {vybrany.pocet != null && (
                    <span className="tabular-nums" style={{
                        fontSize: kompaktni ? 11.5 : 10.5, fontWeight: 700, padding: '0 5px',
                        borderRadius: 999, background: `${barva}2e`, color: barva,
                    }}>{vybrany.pocet}</span>
                )}
                <ChevronDown size={kompaktni ? 15 : 13} style={{ opacity: 0.8 }} />
            </button>

            {otevreno && pozice && createPortal(
                <div ref={panelRef} role="menu" aria-label="Pohled" style={{
                    position: 'fixed', zIndex: 9999, left: pozice.left, top: pozice.top, width: pozice.sirka,
                    maxHeight: Math.max(240, pozice.maxVyska), overflowY: 'auto',
                    ...MODAL_SKLO, padding: '4px 0 6px',
                }}>
                    {skupiny.map((sk, i) => (
                        <div key={sk.nazev || `bez-${i}`}>
                            {i > 0 && <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)', margin: '6px 0' }} />}
                            {sk.nazev && (
                                <div className="text-[9.5px] font-bold uppercase tracking-wider"
                                    style={{ color: mutedColor, padding: '8px 12px 4px' }}>
                                    {sk.nazev}
                                </div>
                            )}
                            {sk.polozky.map(p => {
                                const aktivni = p.id === vybrany.id;
                                const hlasi = p.upozorneni && p.barva && (p.pocet ?? 0) > 0;
                                return (
                                    <button key={p.id} type="button" role="menuitemradio" aria-checked={aktivni}
                                        onClick={() => vyber(p.id)} title={p.title || p.label}
                                        className="flex items-center w-full text-left"
                                        style={{
                                            gap: 8, padding: kompaktni ? '10px 12px' : '7px 12px',
                                            fontSize: kompaktni ? 14 : 12.5, cursor: 'pointer', border: 'none',
                                            background: aktivni ? `${barva}24` : 'transparent',
                                            color: aktivni ? barva : 'var(--text-primary)',
                                            fontWeight: aktivni ? 600 : 400,
                                        }}
                                        onMouseEnter={e => { if (!aktivni) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                                        onMouseLeave={e => { if (!aktivni) e.currentTarget.style.background = 'transparent'; }}>
                                        <span style={{ width: 14, flexShrink: 0, display: 'inline-flex' }}>
                                            {aktivni ? <Check size={14} strokeWidth={2.6} />
                                                : p.Icon ? <p.Icon size={14} style={{ color: hlasi ? p.barva : mutedColor }} /> : null}
                                        </span>
                                        <span className="flex-1 min-w-0 truncate">{p.label}</span>
                                        {p.pocet != null && (
                                            <span className="tabular-nums" style={{
                                                fontSize: 11, fontWeight: 600,
                                                color: aktivni ? barva : hlasi ? p.barva : mutedColor,
                                            }}>{p.pocet}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>,
                document.body,
            )}
        </>
    );
}

/**
 * Zkratka na pohled, který hlásí problém. V úzkém sloupci jen ikona a počet —
 * popisek by řádek zalomil a je v nabídce i v popisku tlačítka.
 */
function PohledZkratka({ pohled, siroky = false, kompaktni = false, onClick }) {
    const popis = `${pohled.label} — ${pohled.pocet}`;
    return (
        <button type="button" onClick={onClick} title={popis}
            aria-label={siroky ? undefined : popis}
            className="inline-flex items-center rounded-full font-bold tabular-nums"
            style={{
                gap: siroky ? 6 : 4, flexShrink: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                height: kompaktni ? 32 : 26, padding: siroky ? '0 11px' : '0 8px',
                fontSize: kompaktni ? 12 : 11,
                border: `1px solid ${pohled.barva}6b`,
                background: `${pohled.barva}21`,
                color: pohled.barva,
                WebkitTapHighlightColor: 'transparent',
            }}>
            {pohled.Icon && <pohled.Icon size={12} />}
            {siroky && <span style={{ fontWeight: 600 }}>{pohled.label}</span>}
            {pohled.pocet}
        </button>
    );
}

/**
 * Pill předdefinovaného pohledu — vždy je aktivní právě jeden. Počet položek
 * v pohledu je součástí pillu. `upozorneni` + `barva` = pohled, který hlásí
 * problém (např. marže pod prahem) — jeho ikona je barevná i neaktivní.
 */
export function PohledPill({ Icon, label, pocet = null, aktivni = false, upozorneni = false, onClick, title, barva = '#F59E0B', isDark, mutedColor, kompaktni = false }) {
    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    // Pohled, který hlásí problém a něco v něm je, nese počet v barvě — i když
    // zrovna aktivní není. Dokud byl počet vždy v neutrální šedé, vypadalo
    // „Blokuje schválení 21“ stejně jako „Zaplaceno 412“ a oko po něm sklouzlo;
    // naléhavost pak musel nést zvláštní pruh nad seznamem, který ukrajoval
    // řádky. Barva je levnější — pilulkový řádek na obrazovce už je.
    const hlasi = upozorneni && pocet > 0;
    return (
        // Pohledy jsou na mobilu hlavní nástroj kontroly („co nemá cenu",
        // „kde chybí alergeny") — a přitom to byl nejmenší a nejtěsnější prvek
        // na obrazovce (25 px, 6 px od sebe). Na dotyku dostanou výšku na prst.
        <button type="button" onClick={onClick} title={title || label} aria-pressed={aktivni}
            className={`inline-flex items-center rounded-full font-medium ${kompaktni ? 'text-[12.5px]' : 'text-[11.5px]'}`}
            style={{
                gap: kompaktni ? 6 : 5,
                padding: kompaktni ? '0 14px' : '3px 10px',
                minHeight: kompaktni ? 36 : undefined,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                WebkitTapHighlightColor: 'transparent',
                border: `1px solid ${aktivni ? `${barva}88` : hranice}`,
                background: aktivni ? `${barva}1f` : 'transparent',
                color: aktivni ? barva : (isDark ? 'rgba(255,255,255,0.72)' : 'var(--text-secondary)'),
                transition: 'background 0.12s ease, border-color 0.12s ease',
            }}
            onMouseEnter={e => { if (!aktivni) e.currentTarget.style.borderColor = `${barva}66`; }}
            onMouseLeave={e => { if (!aktivni) e.currentTarget.style.borderColor = hranice; }}>
            {/* Ikona v barvě i mimo aktivní stav jen u pohledu, který hlásí problém a něco v něm je */}
            {Icon && <Icon size={12} style={{ color: aktivni || hlasi ? barva : mutedColor }} />}
            {label}
            {pocet != null && (
                <span className="tabular-nums" style={{
                    fontSize: 10.5, fontWeight: 600, minWidth: 16, textAlign: 'center',
                    padding: '0 5px', borderRadius: 999,
                    background: aktivni ? `${barva}2e` : hlasi ? `${barva}1f` : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'),
                    color: aktivni || hlasi ? barva : mutedColor,
                }}>{pocet}</span>
            )}
        </button>
    );
}
